// src/components/Dashboard.js
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getCountryFlag, getCountryName } from '../utils/countries';
import { handleShare, getShareOptions } from '../utils/shareUtils';

// Funciones para manejar oraciones diarias
const getPrayedTodayKey = (prayerId) => `prayed_${prayerId}_${new Date().toDateString()}`;

const hasUserPrayedToday = (prayerId) => {
  const key = getPrayedTodayKey(prayerId);
  return localStorage.getItem(key) === 'true';
};

// Componente Modal de opciones de compartir
function ShareModal({ prayer, isOpen, onClose }) {
  const [copying, setCopying] = useState(null);
  
  if (!isOpen) return null;

  const shareOptions = getShareOptions().filter(option => option.available);
  const primaryOptions = shareOptions.filter(option => option.primary);
  const secondaryOptions = shareOptions.filter(option => !option.primary);

  const handleOptionClick = async (method) => {
    if (copying === method) return;

    setCopying(method);
    try {
      await handleShare(prayer, method);
      
      if (method === 'copy-link' || method === 'copy-text') {
        // Mostrar feedback visual para copiar
        setTimeout(() => {
          setCopying(null);
        }, 1500);
      } else {
        setCopying(null);
        onClose();
      }
    } catch (error) {
      console.error('Error al compartir:', error);
      alert('Error al compartir. Inténtalo de nuevo.');
      setCopying(null);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={e => e.stopPropagation()}>
        <div className="share-modal-header">
          <h3>Compartir pedido de oración</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="share-modal-content">
          <div className="prayer-preview">
            <strong>{prayer.nombre}</strong>
            <span>{getCountryFlag(prayer.pais)} {getCountryName(prayer.pais)}</span>
            <p>"{prayer.comentario}"</p>
          </div>

          <div className="share-options">
            <div className="primary-options">
              {primaryOptions.map(option => (
                <button
                  key={option.id}
                  className={`share-option-button ${copying === option.id ? 'copying' : ''}`}
                  onClick={() => handleOptionClick(option.id)}
                  disabled={copying === option.id}
                >
                  <span className="option-icon">{option.icon}</span>
                  <span className="option-label">
                    {copying === option.id ? (
                      option.id.includes('copy') ? '¡Copiado!' : 'Compartiendo...'
                    ) : (
                      option.label
                    )}
                  </span>
                </button>
              ))}
            </div>

            {secondaryOptions.length > 0 && (
              <>
                <div className="share-divider">
                  <span>Más opciones</span>
                </div>
                <div className="secondary-options">
                  {secondaryOptions.map(option => (
                    <button
                      key={option.id}
                      className="share-option-button secondary"
                      onClick={() => handleOptionClick(option.id)}
                    >
                      <span className="option-icon">{option.icon}</span>
                      <span className="option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard({
  prayers,
  stats,
  onPrayForThis,
  hasMore = false,
  loadingOlder = false,
  loadOlderError = false,
  onLoadOlder,
  onRetryLoadOlder
}) {
  const [shareModal, setShareModal] = useState({ isOpen: false, prayer: null });
  const [sharingPrayer, setSharingPrayer] = useState(null);
  const [animatingPrayer, setAnimatingPrayer] = useState(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const sentinelRef = useRef(null);
  const endSentinelRef = useRef(null);
  const prevPrayersLengthRef = useRef(prayers.length);
  const enteringIdsRef = useRef(new Set());
  // Altura del documento justo antes de solicitar una pagina anterior.
  // Al aplicarse el DOM con los pedidos nuevos, empujamos el scroll por el
  // delta exacto para que el usuario siga viendo los MISMOS cards y el
  // sentinel quede fuera del viewport (evita re-triggers del observer).
  const scrollAnchorRef = useRef(null);
  const [, forceRender] = useState(0);

  // Auto-scroll al fondo al montar para mostrar el pedido mas reciente
  // (estilo chat). Con DOM en orden natural (antiguos arriba, nuevos abajo)
  // el fondo del documento es el pedido mas reciente.
  useEffect(() => {
    if (prayers.length > 0 && !initialScrollDone) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, document.documentElement.scrollHeight);
        });
      });
      setInitialScrollDone(true);
    }
  }, [prayers, initialScrollDone]);

  // Scroll anchoring MANUAL: se ejecuta de forma sincrona tras el commit del
  // DOM y ANTES del paint, empujando el scroll por la altura exacta agregada
  // al contenedor. Asi el usuario sigue viendo los MISMOS cards, y el sentinel
  // (al principio del contenedor) queda fuera del viewport.
  useLayoutEffect(() => {
    const prevLen = prevPrayersLengthRef.current;
    const currLen = prayers.length;

    if (currLen > prevLen && scrollAnchorRef.current != null) {
      const prevScrollHeight = scrollAnchorRef.current;
      const scrollingEl = document.scrollingElement || document.documentElement;
      const newScrollHeight = scrollingEl.scrollHeight;
      const delta = newScrollHeight - prevScrollHeight;
      if (delta > 0) {
        // Asignacion directa (mas sincrona que scrollBy), evita cualquier
        // frame intermedio donde el viewport se vea desplazado.
        scrollingEl.scrollTop = scrollingEl.scrollTop + delta;
      }
      scrollAnchorRef.current = null;
    }
  }, [prayers]);

  // Animacion de entrada para pedidos anteriores recien anexados.
  useEffect(() => {
    const prevLen = prevPrayersLengthRef.current;
    const currLen = prayers.length;

    if (currLen > prevLen && !loadingOlder) {
      // El array viene en orden DESC (nuevos primero). fetchOlderPrayers hace
      // APPEND al final del array DESC (son los mas antiguos). Los marcamos
      // para la animacion de entrada.
      const newIds = prayers.slice(prevLen).map((p) => p.id);
      newIds.forEach((newId) => enteringIdsRef.current.add(newId));
      forceRender((n) => n + 1);
      window.setTimeout(() => {
        newIds.forEach((newId) => enteringIdsRef.current.delete(newId));
        forceRender((n) => n + 1);
      }, 500);
    }

    prevPrayersLengthRef.current = currLen;
  }, [prayers, loadingOlder]);

  // Refs sincronizados con las props cambiantes para que el observer NO se
  // recree en cada cambio de estado. Sin esto, al terminar la carga
  // (`loadingOlder: true -> false`) React recrea `onLoadOlder` en el padre,
  // nosotros re-creamos el observer, y al observar un elemento ya visible,
  // el observer dispara callback inmediatamente con isIntersecting=true,
  // causando un loop de cargas encadenadas.
  const loadingOlderRef = useRef(loadingOlder);
  const onLoadOlderRef = useRef(onLoadOlder);
  // Lock SINCRONICO: entre el momento en que el observer dispara
  // `onLoadOlder()` y el momento en que React propaga `loadingOlder=true`
  // hasta nuestro ref, pasa al menos un tick. Si el observer vuelve a emitir
  // un callback en ese intervalo (scroll en curso, re-layout), no podemos
  // confiar solo en `loadingOlderRef.current`. Este ref se marca de forma
  // inmediata en el callback y se libera cuando la carga termina.
  const isRequestingOlderRef = useRef(false);
  useEffect(() => {
    loadingOlderRef.current = loadingOlder;
    if (!loadingOlder) {
      isRequestingOlderRef.current = false;
    }
  }, [loadingOlder]);
  useEffect(() => { onLoadOlderRef.current = onLoadOlder; }, [onLoadOlder]);

  // IntersectionObserver sobre el sentinel (visualmente arriba del contenedor).
  // Solo se recrea si cambia `hasMore` o `loadOlderError`. El callback lee el
  // estado actual desde los refs, evitando closures obsoletas.
  useEffect(() => {
    if (!hasMore || loadOlderError) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !loadingOlderRef.current &&
          !isRequestingOlderRef.current
        ) {
          isRequestingOlderRef.current = true;
          // Guardamos la altura ANTES de disparar la carga para aplicar
          // el scroll anchoring manual cuando lleguen los datos.
          const scrollingEl = document.scrollingElement || document.documentElement;
          scrollAnchorRef.current = scrollingEl.scrollHeight;
          if (typeof onLoadOlderRef.current === 'function') {
            onLoadOlderRef.current();
          }
        }
      },
      { rootMargin: '300px 0px 0px 0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadOlderError]);

  // IntersectionObserver sobre el sentinel del FINAL (debajo del ultimo card).
  // Si el sentinel es visible => usuario esta viendo el pedido mas reciente =>
  // ocultamos el boton flotante. Si no es visible => mostrarlo.
  // `rootMargin` inferior positivo: el boton se oculta apenas el sentinel este
  // a <=120px del fondo del viewport (colchon suave para evitar parpadeo).
  useEffect(() => {
    const endSentinel = endSentinelRef.current;
    if (!endSentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setShowScrollToBottom(!entry.isIntersecting);
      },
      { rootMargin: '0px 0px 120px 0px', threshold: 0 }
    );

    observer.observe(endSentinel);
    return () => observer.disconnect();
  }, [prayers.length]);

  const handleScrollToBottom = () => {
    const scrollingEl = document.scrollingElement || document.documentElement;
    scrollingEl.scrollTo({
      top: scrollingEl.scrollHeight,
      behavior: 'smooth'
    });
  };

  const handleManualRetry = () => {
    if (typeof onRetryLoadOlder === 'function') {
      onRetryLoadOlder();
    }
  };

  const handleShareClick = async (prayer) => {
    setSharingPrayer(prayer.id);
    
    try {
      const result = await handleShare(prayer, 'auto');
      
      if (result === 'show-options') {
        // Mostrar modal con opciones
        setShareModal({ isOpen: true, prayer });
      } else if (result) {
        // Compartir exitoso (nativo)
        console.log('Compartido exitosamente');
      }
    } catch (error) {
      console.error('Error al compartir:', error);
      // En caso de error, mostrar opciones
      setShareModal({ isOpen: true, prayer });
    } finally {
      setSharingPrayer(null);
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, prayer: null });
  };

  const handlePrayClick = async (prayerId) => {
    // ✨ OPTIMISTIC UPDATE: Activar animación INMEDIATAMENTE
    setAnimatingPrayer(prayerId);
    
    // Remover animación después de que termine (600ms)
    setTimeout(() => {
      setAnimatingPrayer(null);
    }, 600);
    
    // Luego llamar al servidor en background
    await onPrayForThis(prayerId);
  };

  return (
    <div className="dashboard">
      {/* Events y Stats ahora se muestran en InfoPanel */}

      {/* Indicador flotante de carga de pedidos anteriores: NO afecta el flujo
          del layout (position: fixed), evitando saltos al aparecer/desaparecer. */}
      {loadingOlder && (
        <div className="load-older-floating" role="status" aria-live="polite">
          <span className="load-more-spinner"></span>
          <span>Cargando pedidos anteriores...</span>
        </div>
      )}

      <div className="prayers-list">
        <h3>Comunidad de Oración</h3>
        {Array.isArray(prayers) ? (
          <div className="prayer-cards-container">
            {/* Sentinel y mensajes de cabecera: van al principio del DOM para
                quedar visualmente ARRIBA (orden natural). El scroll anchoring
                nativo del navegador compensa automaticamente cuando insertamos
                nuevos cards aqui. */}
            {hasMore && !loadOlderError && (
              <div ref={sentinelRef} className="load-more-sentinel" aria-hidden="true" />
            )}

            {loadOlderError && !loadingOlder && (
              <div className="load-more-status load-more-status--error">
                <p className="load-more-error-text">
                  No pudimos cargar pedidos anteriores.
                </p>
                <button
                  type="button"
                  className="load-more-retry-button"
                  onClick={handleManualRetry}
                >
                  Reintentar
                </button>
              </div>
            )}

            {!hasMore && !loadingOlder && prayers.length > 0 && (
              <div className="end-of-list-message">
                Has llegado al inicio — {stats?.total ?? prayers.length} pedidos en total
              </div>
            )}

            {/* Array viene en orden DESC (nuevos primero). Invertimos al
                renderizar para mostrar los mas antiguos arriba y los mas
                recientes abajo, en orden DOM natural. */}
            {prayers.slice().reverse().map((prayer, index) => {
              const isPending = prayer.isPending === true;
              return (
              <div
                key={prayer.id || index}
                data-prayer-id={prayer.id}
                className={`prayer-card${enteringIdsRef.current.has(prayer.id) ? ' prayer-card--enter' : ''}${isPending ? ' prayer-card--pending' : ''}`}
              >
                <div className="prayer-header">
                  <strong>{prayer.nombre}</strong>
                  <div className="prayer-meta">
                    <span>{getCountryFlag(prayer.pais)} {getCountryName(prayer.pais)}</span>
                    <span>{prayer.fecha}</span>
                    {prayer.categoria && (
                      <span className={`category-badge category-${prayer.categoria?.toLowerCase()}`}>
                        {prayer.categoria}
                      </span>
                    )}
                    {isPending && (
                      <span className="pending-badge" aria-live="polite">
                        <span className="load-more-spinner"></span>
                        Enviando...
                      </span>
                    )}
                  </div>
                </div>
                <div className="prayer-content">
                  {prayer.comentario}
                </div>
                
                {/* Contador estilo WhatsApp */}
                {prayer.oraciones > 0 && (
                  <div className="prayer-reaction">
                    <span className={`reaction-badge ${animatingPrayer === prayer.id ? 'bounce-animation' : ''}`}>
                      🙏 {prayer.oraciones}
                    </span>
                  </div>
                )}
                
                <div className="prayer-actions">
                  <button 
                    className={`pray-button ${hasUserPrayedToday(prayer.id) ? 'prayed-today' : ''}`}
                    onClick={() => handlePrayClick(prayer.id)}
                    disabled={isPending || hasUserPrayedToday(prayer.id)}
                  >
                    {hasUserPrayedToday(prayer.id) ? 'Ya oraste hoy' : 'Orar por esto'}
                  </button>
                  <button 
                    className={`share-button ${sharingPrayer === prayer.id ? 'sharing' : ''}`}
                    onClick={() => handleShareClick(prayer)}
                    disabled={isPending || sharingPrayer === prayer.id}
                  >
                    {sharingPrayer === prayer.id ? 'Compartiendo...' : 'Compartir'}
                  </button>
                </div>
              </div>
              );
            })}
            {/* Sentinel del final: observado para decidir si mostrar el FAB
                "Ir al pedido mas reciente". No ocupa espacio visible. */}
            <div ref={endSentinelRef} className="scroll-end-sentinel" aria-hidden="true" />
          </div>
        ) : (
          <div className="loading-message">Cargando pedidos de oración...</div>
        )}
      </div>

      {/* Boton flotante: ir al pedido mas reciente. Renderizado via Portal
          directamente en <body> para garantizar que `position: fixed` se ancle
          al viewport y no a un contenedor con `backdrop-filter` (que crea un
          containing block y rompe el posicionamiento fixed). */}
      {createPortal(
        <button
          type="button"
          className={`scroll-to-bottom-fab${showScrollToBottom ? ' scroll-to-bottom-fab--visible' : ''}`}
          onClick={handleScrollToBottom}
          aria-label="Ir al pedido más reciente"
          aria-hidden={!showScrollToBottom}
          tabIndex={showScrollToBottom ? 0 : -1}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>,
        document.body
      )}

      <ShareModal 
        prayer={shareModal.prayer}
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
      />

    </div>
  );
}

export default Dashboard;